import { Injectable } from '@angular/core';
import { Canvas, Circle, Rectangle, Triangle, Image, Text } from '@models';

const RECTANGLE_RADIUS = 12;

@Injectable({
  providedIn: 'root',
})
export class DrawService {
  private getCanvas(targetCanvas: Canvas): CanvasRenderingContext2D {
    const canvas: HTMLCanvasElement = document.getElementById(
      targetCanvas === 'static' ? 'overlayCanvas' : 'dynamicCanvas',
    ) as HTMLCanvasElement;
    return canvas.getContext('2d');
  }

  clearCanvas(targetCanvas: Canvas) {
    const canvas: HTMLCanvasElement = document.getElementById(
      targetCanvas === 'static' ? 'overlayCanvas' : 'dynamicCanvas',
    ) as HTMLCanvasElement;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  }

  drawRectangle(rectangle: Rectangle, canvas: Canvas) {
    const ctx = this.getCanvas(canvas);
    ctx.globalAlpha = rectangle.transparency ?? 1;
    ctx.fillStyle = rectangle.fillColor;
    ctx.beginPath();
    ctx.roundRect(
      rectangle.x,
      rectangle.y,
      rectangle.width,
      rectangle.height,
      RECTANGLE_RADIUS,
    );
    ctx.fill();
    if (rectangle.image) {
      ctx.globalAlpha = 1;
      const image = new Image();
      image.src = rectangle.image;
      image.onload = () => {
        ctx.drawImage(
          image,
          rectangle.x + rectangle.width / 2 - image.width / 2,
          rectangle.y + rectangle.height / 2 - image.height / 2,
          image.width,
          image.height,
        );
      };
    }
  }

  drawCircle(circle: Circle, canvas: Canvas) {
    const ctx = this.getCanvas(canvas);
    ctx.globalAlpha = circle.transparency ?? 1;
    ctx.fillStyle = circle.fillColor;
    ctx.beginPath();
    ctx.arc(circle.x, circle.y, circle.radius, 0, 2 * Math.PI);
    ctx.closePath();
    ctx.fillStyle = circle.fillColor;
    ctx.fill();
    ctx.globalAlpha = 1;
    if (circle.image) {
      const image = new Image();
      image.src = circle.image;
      image.onload = () => {
        ctx.drawImage(
          image,
          circle.x - image.width / 2,
          circle.y - image.height / 2,
          image.width,
          image.height,
        );
      };
    }
    if (circle.value !== undefined) {
      ctx.fillStyle = '#FFFFFF';
      ctx.font = 'bold 32px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(circle.value.toString(), circle.x, circle.y + 2);
    }
  }

  drawImage(image: Image, canvas: Canvas) {
    const ctx = this.getCanvas(canvas);
    const img = new Image();
    img.src = image.image;
    img.onload = () => {
      const width = img.width;
      const height = img.height;
      if (image.scale) {
        ctx.drawImage(
          img,
          image.x,
          image.y,
          width * image.scale,
          height * image.scale,
        );
      } else {
        ctx.drawImage(img, image.x - width / 2, image.y - height / 2);
      }
    };
  }

  drawTriangle(triangle: Triangle, canvas: Canvas) {
    const ctx = this.getCanvas(canvas);
    ctx.globalAlpha = triangle.transparency ?? 1;
    ctx.beginPath();
    const angle =
      triangle.angle +
      (triangle.direction === 'ccw' ? Math.PI : 0) -
      Math.PI / 2;
    const x1 = triangle.x + triangle.size * Math.cos(angle);
    const y1 = triangle.y + triangle.size * Math.sin(angle);
    const x2 = triangle.x + triangle.size * Math.cos(angle + (2 * Math.PI) / 3);
    const y2 = triangle.y + triangle.size * Math.sin(angle + (2 * Math.PI) / 3);
    const x3 = triangle.x + triangle.size * Math.cos(angle + (4 * Math.PI) / 3);
    const y3 = triangle.y + triangle.size * Math.sin(angle + (4 * Math.PI) / 3);
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.lineTo(x3, y3);
    ctx.closePath();
    ctx.fillStyle = triangle.fillColor;
    ctx.fill();
  }

  drawText(text: Text, canvas: Canvas) {
    const ctx = this.getCanvas(canvas);
    ctx.globalAlpha = text.transparency ?? 1;
    const fontSize = text.fontSize ?? 20;
    ctx.font = fontSize + 'px sans-serif';
    ctx.fillStyle = text.fillColor;
    ctx.fillText(
      text.text,
      text.x - ctx.measureText(text.text).width / 2,
      text.y - fontSize / 2,
    );
  }
}
